import { useState } from "react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { StudentTeacherHistoryModal } from "@/components/admin/teacher-stats/StudentTeacherHistoryModal";

interface NextTopup {
  created_at: string;
  value: number;
  remark: string | null;
}

interface CrmCallInfo {
  renewal_status: 'UNDECIDED' | 'WILL_RENEW' | 'NOT_RENEWING' | 'RENEWED';
  linked_order_code: string | null;
  call_date: string;
}

interface StudentResult {
  student_code: string;
  student_name: string;
  nickname: string | null;
  status: 'depleted' | 'nearly_depleted' | 'predicted';
  event_date: string;
  after_hours_at_event: number;
  current_hours: number | null;
  next_topup: NextTopup | null;
  crm_info: CrmCallInfo | null;
  total_purchase: number | null;
  estimated_hours_depletion_date: string | null;
  estimated_8hours_date: string | null;
  weekly_scheduled_hours: number | null;
}

export type SortField = 'student_code' | 'event_date' | 'after_hours_at_event' | 'current_hours' | 'estimated_hours_depletion_date' | null;
export type SortDirection = 'asc' | 'desc';

interface DepletionTableProps {
  students: StudentResult[] | undefined;
  isLoading: boolean;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}

interface SortableHeaderProps {
  field: Exclude<SortField, null>;
  label: string;
  currentField: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
  align?: 'left' | 'right';
}

function SortableHeader({ field, label, currentField, direction, onSort, align = 'left' }: SortableHeaderProps) {
  const isActive = field === currentField;
  return (
    <TableHead
      className={cn("cursor-pointer hover:bg-muted/50 transition-colors select-none", align === 'right' && "text-right")}
      onClick={() => onSort(field)}
    >
      <div className={cn("flex items-center gap-1", align === 'right' && "justify-end")}>
        {label}
        {isActive ? (
          direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronsUpDown className="h-4 w-4 opacity-30" />
        )}
      </div>
    </TableHead>
  );
}

function CrmStatusBadge({ crmInfo }: { crmInfo: CrmCallInfo | null }) {
  if (!crmInfo) return <span className="text-muted-foreground text-sm">-</span>;
  switch (crmInfo.renewal_status) {
    case 'RENEWED':
      return <Badge className="bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200 whitespace-nowrap">✅ ต่อแล้ว ({crmInfo.linked_order_code})</Badge>;
    case 'WILL_RENEW':
      return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-200">🔵 ต้องการต่อ</Badge>;
    case 'UNDECIDED':
      return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-200">🟡 ยังไม่ตัดสินใจ</Badge>;
    case 'NOT_RENEWING':
      return <Badge variant="destructive">🔴 ไม่ต้องการต่อ</Badge>;
    default:
      return <span className="text-muted-foreground text-sm">-</span>;
  }
}

function StatusBadge({ status }: { status: 'depleted' | 'nearly_depleted' | 'predicted' }) {
  switch (status) {
    case 'depleted':
      return <Badge variant="destructive">🔴 หมด</Badge>;
    case 'nearly_depleted':
      return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-200">🟡 ใกล้หมด</Badge>;
    case 'predicted':
      return <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100 dark:bg-purple-900 dark:text-purple-200">🔮 คาดว่าจะหมด</Badge>;
  }
}

const headerColumns = [
  "รหัสนักเรียน", "ชื่อเล่น", "ชื่อเต็ม", "สถานะ", "วันที่หมด/ใกล้หมด",
  "คาดว่าจะหมด", "ชม.ณ เวลานั้น", "ชม.ปัจจุบัน", "เติมชม.ล่าสุด", "ยอดซื้อรวม", "สถานะ CRM", "วันที่โทร"
];

export function DepletionTable({ students, isLoading, sortField, sortDirection, onSort }: DepletionTableProps) {
  const [selectedStudentCode, setSelectedStudentCode] = useState<string | null>(null);

  const formatDate = (dateString: string) => {
    try { return format(new Date(dateString), "dd/MM/yy"); } catch { return dateString; }
  };

  if (isLoading) {
    return (
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              {headerColumns.map((h) => <TableHead key={h}>{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {[1, 2, 3, 4, 5].map((i) => (
              <TableRow key={i}>
                {headerColumns.map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (!students || students.length === 0) {
    return <div className="border rounded-lg p-8 text-center text-muted-foreground">ไม่พบข้อมูลนักเรียนในช่วงเวลาที่เลือก</div>;
  }

  return (
    <>
      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader field="student_code" label="รหัสนักเรียน" currentField={sortField} direction={sortDirection} onSort={onSort} />
              <TableHead>ชื่อเล่น</TableHead>
              <TableHead>ชื่อเต็ม</TableHead>
              <TableHead>สถานะ</TableHead>
              <SortableHeader field="event_date" label="วันที่หมด/ใกล้หมด" currentField={sortField} direction={sortDirection} onSort={onSort} />
              <SortableHeader field="estimated_hours_depletion_date" label="คาดว่าจะหมด" currentField={sortField} direction={sortDirection} onSort={onSort} />
              <SortableHeader field="after_hours_at_event" label="ชม.ณ เวลานั้น" currentField={sortField} direction={sortDirection} onSort={onSort} align="right" />
              <SortableHeader field="current_hours" label="ชม.ปัจจุบัน" currentField={sortField} direction={sortDirection} onSort={onSort} align="right" />
              <TableHead>เติมชม.ล่าสุด</TableHead>
              <TableHead className="text-right">ยอดซื้อรวม</TableHead>
              <TableHead>สถานะ CRM</TableHead>
              <TableHead>วันที่โทร</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {students.map((student, index) => (
              <TableRow
                key={`${student.student_code}-${index}`}
                onClick={() => setSelectedStudentCode(student.student_code)}
                className="cursor-pointer hover:bg-muted/50"
              >
                <TableCell className="font-mono text-sm">{student.student_code}</TableCell>
                <TableCell className="font-medium">{student.nickname || "-"}</TableCell>
                <TableCell>{student.student_name}</TableCell>
                <TableCell><StatusBadge status={student.status} /></TableCell>
                <TableCell>{student.status === 'predicted' ? '-' : formatDate(student.event_date)}</TableCell>
                <TableCell>
                  {student.estimated_hours_depletion_date ? (
                    <div>
                      <div>{formatDate(student.estimated_hours_depletion_date)}</div>
                      <div className="text-xs text-muted-foreground">
                        {student.weekly_scheduled_hours != null && student.weekly_scheduled_hours > 0
                          ? `เรียน ${student.weekly_scheduled_hours}h/week`
                          : '🔴 ไม่มีตารางประจำ'}
                      </div>
                    </div>
                  ) : (
                    <span className="text-sm">
                      {student.weekly_scheduled_hours != null && student.weekly_scheduled_hours > 0
                        ? '⚠️ ข้อมูลผิดพลาด'
                        : '🔴 ไม่มีตารางประจำ'}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {student.status === 'predicted' ? '-' : student.after_hours_at_event.toFixed(1)}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {student.current_hours !== null ? student.current_hours.toFixed(1) : "-"}
                </TableCell>
                <TableCell>
                  {student.next_topup ? (
                    <span className="text-green-600 dark:text-green-400">
                      +{student.next_topup.value} ({formatDate(student.next_topup.created_at)})
                    </span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-right text-sm">
                  {student.total_purchase != null
                    ? <span className="font-medium">{new Intl.NumberFormat('th-TH').format(student.total_purchase)}</span>
                    : <span className="text-muted-foreground">-</span>}
                </TableCell>
                <TableCell><CrmStatusBadge crmInfo={student.crm_info} /></TableCell>
                <TableCell className="text-sm">
                  {student.crm_info?.call_date
                    ? format(new Date(student.crm_info.call_date), "dd/MM/yy")
                    : <span className="text-muted-foreground">-</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <StudentTeacherHistoryModal studentCode={selectedStudentCode} onClose={() => setSelectedStudentCode(null)} />
    </>
  );
}